import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Phone, RotateCcw, PartyPopper } from 'lucide-react';
import * as playApi from '../api/playApi';
import BoxGrid from '../components/game/BoxGrid';
import PrizeBoard from '../components/game/PrizeBoard';
import RevealAnimation from '../components/game/RevealAnimation';
import GameHeader from '../components/game/GameHeader';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

/* ── Helpers ── */
function formatEuro(value) {
  if (typeof value !== 'number') return String(value);
  return value.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: value < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

/* ── Inline styles ── */
const S = {
  page: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-bg-primary)',
    position: 'relative',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'var(--font-body)',
    fontSize: '0.75rem',
    color: 'var(--color-text-muted)',
    textDecoration: 'none',
    zIndex: 10,
    transition: 'color var(--transition-fast)',
    padding: '6px 16px',
    backgroundColor: 'rgba(5,5,20,0.8)',
    borderBottom: '1px solid rgba(212,175,55,0.06)',
  },

  /* Intro */
  introWrap: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-xl)',
    textAlign: 'center',
  },
  introTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(2rem, 6vw, 3.5rem)',
    fontWeight: 700,
    color: 'var(--color-primary)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    margin: 0,
    textShadow: '0 0 40px rgba(212,175,55,0.3)',
  },
  introSub: {
    fontFamily: 'var(--font-body)',
    fontSize: '1.05rem',
    color: 'var(--color-text-secondary)',
    marginTop: 'var(--space-md)',
    marginBottom: 'var(--space-xl)',
    maxWidth: '440px',
  },
  introForm: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-md)',
    width: '100%',
    maxWidth: '360px',
  },

  /* Header bar */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--space-md) var(--space-xl)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderBottom: '1px solid rgba(212,175,55,0.1)',
    flexWrap: 'wrap',
    gap: 'var(--space-sm)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-lg)',
  },
  headerTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.3rem',
    fontWeight: 700,
    color: 'var(--color-primary)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    margin: 0,
    textShadow: '0 0 20px rgba(212,175,55,0.2)',
  },
  headerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-md)',
  },
  headerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '0.85rem',
    color: 'var(--color-text-secondary)',
  },

  /* Main game layout */
  gameLayout: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    minHeight: 'calc(100vh - 60px)',
    gap: 0,
  },
  sidebar: {
    borderRight: '1px solid rgba(212,175,55,0.08)',
    overflowY: 'auto',
    paddingTop: 'var(--space-md)',
    paddingBottom: 'var(--space-md)',
    background: 'linear-gradient(180deg, rgba(5,5,16,0.95) 0%, rgba(10,10,30,0.98) 100%)',
  },
  mainArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-md) var(--space-lg)',
    gap: 'var(--space-md)',
    overflowY: 'auto',
  },

  /* Message banner */
  messageBanner: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'var(--color-primary)',
    textAlign: 'center',
    letterSpacing: '0.04em',
    padding: 'var(--space-sm) var(--space-lg)',
    backgroundColor: 'rgba(212,175,55,0.06)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(212,175,55,0.12)',
    width: '100%',
    maxWidth: '680px',
  },

  /* Offer overlay */
  offerOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 900,
    gap: 'var(--space-xl)',
    padding: 'var(--space-xl)',
  },
  offerLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    fontFamily: 'var(--font-display)',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  offerAmount: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(3rem, 8vw, 5rem)',
    fontWeight: 700,
    color: 'var(--color-primary)',
    textShadow: '0 0 50px rgba(212,175,55,0.5)',
    letterSpacing: '0.02em',
  },
  offerButtons: {
    display: 'flex',
    gap: 'var(--space-lg)',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },

  /* Final screen */
  finalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(5,5,20,0.95)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 950,
    gap: 'var(--space-lg)',
    padding: 'var(--space-xl)',
    textAlign: 'center',
  },
  finalIcon: {
    color: 'var(--color-primary)',
    marginBottom: 'var(--space-sm)',
  },
  finalTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    margin: 0,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  finalPrize: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(3rem, 8vw, 5rem)',
    fontWeight: 700,
    color: 'var(--color-primary)',
    margin: 0,
    textShadow: '0 0 60px rgba(212,175,55,0.5)',
  },
  finalDetail: {
    fontFamily: 'var(--font-body)',
    fontSize: '1.1rem',
    color: 'var(--color-text-secondary)',
    margin: 0,
    lineHeight: 1.7,
  },

  /* Selection phase */
  selectionWrap: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-xl)',
    gap: 'var(--space-lg)',
  },
  selectionTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
    fontWeight: 700,
    color: 'var(--color-primary)',
    textAlign: 'center',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    margin: 0,
    textShadow: '0 0 30px rgba(212,175,55,0.2)',
  },

  /* Responsive overrides inserted via media query style tag */
};

const responsiveCSS = `
  @media (max-width: 768px) {
    .sp-game-layout {
      grid-template-columns: 1fr !important;
      min-height: auto !important;
    }
    .sp-sidebar {
      border-right: none !important;
      border-bottom: 1px solid rgba(212,175,55,0.08) !important;
      max-height: 180px !important;
      overflow-y: auto !important;
      padding: 8px 4px !important;
    }
    .sp-top-bar {
      padding: 10px 12px !important;
    }
    .sp-top-bar a, .sp-top-bar button {
      font-size: 0.85rem !important;
      padding: 6px 12px !important;
      background: rgba(255,255,255,0.06) !important;
      border-radius: 8px !important;
    }
    .sp-main-area {
      padding: 8px 8px !important;
      gap: 8px !important;
    }
    .sp-msg-banner {
      font-size: 0.9rem !important;
      padding: 6px 12px !important;
    }
  }
`;

/* ── Phases ── */
const PHASE = {
  INTRO: 'intro',
  SELECT: 'select',
  PLAYING: 'playing',
  OFFER: 'offer',
  FINAL: 'final',
};

export default function SinglePlayerGame() {
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [playerName, setPlayerName] = useState('');
  const [gameCode, setGameCode] = useState(() => sessionStorage.getItem('sp_game_code') || '');
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /* reveal overlay */
  const [revealVisible, setRevealVisible] = useState(false);
  const [revealBox, setRevealBox] = useState(null);
  const [revealPrize, setRevealPrize] = useState(null);
  const [revealLabel, setRevealLabel] = useState(null);

  /* offer */
  const [currentOffer, setCurrentOffer] = useState(null);
  const [offerResponding, setOfferResponding] = useState(false);

  /* final */
  const [finalData, setFinalData] = useState(null);

  const openingRef = useRef(false);

  /* ── Restore session ── */
  useEffect(() => {
    const code = sessionStorage.getItem('sp_game_code');
    if (code) {
      setGameCode(code);
      playApi.getGame(code)
        .then((data) => {
          setGame(data);
          if (data.status === 'completed' || data.status === 'cancelled') {
            setFinalData(data);
            setPhase(PHASE.FINAL);
          } else if (!data.player_box_number) {
            setPhase(PHASE.SELECT);
          } else if (data.last_offer && data.last_offer.status === 'pending') {
            setCurrentOffer(data.last_offer);
            setPhase(PHASE.OFFER);
          } else {
            setPhase(PHASE.PLAYING);
          }
        })
        .catch(() => {
          sessionStorage.removeItem('sp_game_code');
          setGameCode('');
          setPhase(PHASE.INTRO);
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Compute round info from API ── */
  const currentRound = game?.current_round || 1;
  const roundInfo = game?.current_round_info;
  const boxesToOpenTotal = roundInfo?.boxes_to_open || 0;
  const boxesOpenedInRound = roundInfo?.boxes_opened || 0;
  const boxesRemaining = Math.max(0, boxesToOpenTotal - boxesOpenedInRound);

  /* ── Handlers ── */
  const handleStart = async (e) => {
    e.preventDefault();
    if (!playerName.trim()) return;
    setLoading(true);
    setError('');
    try {
      const data = await playApi.startGame(playerName.trim());
      const code = data.code || data.game_code;
      setGameCode(code);
      sessionStorage.setItem('sp_game_code', code);
      setGame(data);
      setPhase(PHASE.SELECT);
    } catch (err) {
      console.error('Start error:', err.response?.status, err.response?.data);
      setError(err.response?.data?.message || err.message || 'Errore di avvio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBox = async (boxNumber) => {
    setLoading(true);
    setError('');
    try {
      const data = await playApi.selectBox(gameCode, boxNumber);
      setGame(data);
      setPhase(PHASE.PLAYING);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nella selezione.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBox = useCallback(async (boxNumber) => {
    if (openingRef.current) return;
    openingRef.current = true;
    setError('');
    try {
      const data = await playApi.openBox(gameCode, boxNumber);

      // The response is flat game state with opened_box and optional new_offer
      const prize = data.opened_box?.prize_value;
      const prizeLabel = data.opened_box?.prize_label;

      setRevealBox(boxNumber);
      setRevealPrize(prize ?? 0);
      setRevealLabel(prizeLabel || null);
      setRevealVisible(true);
      setGame(data);

      // Check if game auto-finished (all boxes opened)
      if (data.game_finished || data.status === 'completed') {
        setCurrentOffer(null);
        // Show reveal, then final screen
        return;
      }

      // Check if an offer was auto-generated
      if (data.new_offer) {
        setCurrentOffer(data.new_offer);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nell\'apertura.');
    } finally {
      openingRef.current = false;
    }
  }, [gameCode]);

  const handleRevealDismiss = useCallback(() => {
    setRevealVisible(false);
    setRevealBox(null);
    setRevealPrize(null);
    setRevealLabel(null);

    if (game?.game_finished || game?.status === 'completed') {
      setFinalData(game);
      setPhase(PHASE.FINAL);
    } else if (currentOffer) {
      setPhase(PHASE.OFFER);
    }
  }, [game, currentOffer]);

  const handleOfferRespond = async (accepted, swap = false) => {
    setOfferResponding(true);
    setError('');
    try {
      const swapBoxNum = swap ? currentOffer?.swap_box_number : null;
      const data = await playApi.respondOffer(gameCode, accepted, swap, swapBoxNum);
      setGame(data);
      setCurrentOffer(null);

      if (data.status === 'completed') {
        setFinalData(data);
        setPhase(PHASE.FINAL);
      } else if (data.swap_continued) {
        // Swap happened but game continues
        setPhase(PHASE.PLAYING);
      } else if (accepted) {
        setFinalData(data);
        setPhase(PHASE.FINAL);
      } else {
        setPhase(PHASE.PLAYING);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella risposta.');
    } finally {
      setOfferResponding(false);
    }
  };

  const handlePlayAgain = () => {
    sessionStorage.removeItem('sp_game_code');
    setGameCode('');
    setGame(null);
    setPlayerName('');
    setCurrentOffer(null);
    setFinalData(null);
    setError('');
    setPhase(PHASE.INTRO);
  };

  /* ── Render phases ── */
  return (
    <div style={S.page}>
      <style>{responsiveCSS}</style>

      {/* Reveal animation overlay */}
      <RevealAnimation
        isVisible={revealVisible}
        boxNumber={revealBox}
        prizeValue={revealPrize}
        prizeLabel={revealLabel}
        onComplete={handleRevealDismiss}
      />

      <AnimatePresence mode="wait">
        {/* ─── INTRO ─── */}
        {phase === PHASE.INTRO && (
          <motion.div
            key="intro"
            style={S.introWrap}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/"
              style={{ ...S.backLink, position: 'absolute', top: 16, left: 16, backgroundColor: 'transparent', borderBottom: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
            >
              <ArrowLeft size={14} />
              Home
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 style={S.introTitle}>PickBoxGame</h1>
              <p style={S.introSub}>
                Inserisci il tuo nome e preparati a sfidare la fortuna.
              </p>

              <form onSubmit={handleStart} style={S.introForm}>
                <Input
                  placeholder="Il tuo nome"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  required
                  error={error}
                  style={{ textAlign: 'center', fontSize: '1.1rem' }}
                />
                <Button
                  type="submit"
                  size="lg"
                  loading={loading}
                  disabled={!playerName.trim()}
                  style={{
                    width: '100%',
                    borderRadius: 'var(--radius-full)',
                    letterSpacing: '0.08em',
                  }}
                >
                  Inizia
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* ─── BOX SELECTION ─── */}
        {phase === PHASE.SELECT && game && (
          <motion.div
            key="select"
            style={S.selectionWrap}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <motion.h2
              style={S.selectionTitle}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Scegli il tuo pacco!
            </motion.h2>
            <BoxGrid
              boxes={game.boxes || []}
              onBoxClick={handleSelectBox}
              disabled={loading}
              selectionMode
            />
            {error && (
              <p style={{ color: 'var(--color-danger)', fontSize: '0.9rem', margin: 0 }}>{error}</p>
            )}
          </motion.div>
        )}

        {/* ─── PLAYING ─── */}
        {phase === PHASE.PLAYING && game && (
          <motion.div
            key="playing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
          >
            {/* Top bar */}
            <div className="sp-top-bar" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 16px',
              backgroundColor: 'rgba(5,5,20,0.8)',
              borderBottom: '1px solid rgba(212,175,55,0.06)',
            }}>
              <Link
                to="/"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: 'var(--font-display)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#D4AF37',
                  textDecoration: 'none',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  background: 'rgba(212,175,55,0.08)',
                  border: '1px solid rgba(212,175,55,0.2)',
                  transition: 'all 0.15s ease',
                  letterSpacing: '0.03em',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.15)'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.08)'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.2)'; }}
              >
                <ArrowLeft size={14} />
                Home
              </Link>
              <button
                onClick={handlePlayAgain}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: 'var(--font-display)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#e74c3c',
                  background: 'rgba(231,76,60,0.08)',
                  border: '1px solid rgba(231,76,60,0.2)',
                  borderRadius: '8px',
                  padding: '6px 14px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  letterSpacing: '0.03em',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(231,76,60,0.15)'; e.currentTarget.style.borderColor = 'rgba(231,76,60,0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(231,76,60,0.08)'; e.currentTarget.style.borderColor = 'rgba(231,76,60,0.2)'; }}
              >
                <RotateCcw size={13} />
                Ricomincia
              </button>
            </div>
            {/* Header */}
            <GameHeader game={game} />

            {/* Game body */}
            <div className="sp-game-layout" style={S.gameLayout}>
              {/* Sidebar - Prize Board */}
              <div className="sp-sidebar" style={S.sidebar}>
                <PrizeBoard
                  boxes={game.boxes || []}
                  allPrizes={game.all_prizes || game.prizes || []}
                />
              </div>

              {/* Main */}
              <div className="sp-main-area" style={S.mainArea}>
                {/* Instruction message */}
                <motion.div
                  className="sp-msg-banner"
                  style={S.messageBanner}
                  key={`msg-${currentRound}-${boxesRemaining}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {boxesRemaining > 0
                    ? `Apri ${boxesRemaining} pacc${boxesRemaining === 1 ? 'o' : 'hi'} in questo turno`
                    : 'Turno completato!'}
                </motion.div>

                {/* Box Grid */}
                <BoxGrid
                  boxes={game.boxes || []}
                  playerBoxNumber={game.player_box_number}
                  onBoxClick={handleOpenBox}
                  disabled={loading || revealVisible || boxesRemaining <= 0}
                />

                {error && (
                  <p style={{ color: 'var(--color-danger)', fontSize: '0.9rem', margin: 0 }}>{error}</p>
                )}

                {/* Show last offer info if rejected */}
                {game.last_offer && game.last_offer.status === 'rejected' && (
                  <div style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.8rem',
                    color: 'var(--color-danger)',
                    backgroundColor: 'rgba(231,76,60,0.1)',
                    padding: '6px 14px',
                    borderRadius: 'var(--radius-full)',
                    textDecoration: 'line-through',
                    opacity: 0.7,
                  }}>
                    Ultima offerta rifiutata: {formatEuro(game.last_offer.amount)}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* ─── OFFER ─── */}
        {phase === PHASE.OFFER && currentOffer && (
          <motion.div
            key="offer"
            style={S.offerOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            {currentOffer.swap_only ? (
              <>
                <motion.div
                  style={S.offerLabel}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Phone size={20} />
                  Il Dottore ti propone lo SCAMBIO!
                </motion.div>

                <motion.div
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    textAlign: 'center',
                    lineHeight: 1.5,
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  Vuoi scambiare il tuo pacco con il pacco N. {currentOffer.swap_box_number}
                  {(() => {
                    const regionName = currentOffer.swap_region_name
                      || game?.boxes?.find(b => b.box_number === currentOffer.swap_box_number)?.region_name;
                    return regionName ? ` (${regionName})` : '';
                  })()}?
                </motion.div>

                <motion.div
                  style={S.offerButtons}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  <Button
                    size="lg"
                    loading={offerResponding}
                    onClick={() => handleOfferRespond(false, true)}
                    style={{
                      backgroundColor: 'var(--color-info)',
                      borderColor: 'var(--color-info)',
                      color: '#fff',
                      padding: '16px 48px',
                      fontSize: '1.1rem',
                      borderRadius: 'var(--radius-full)',
                    }}
                  >
                    Scambio!
                  </Button>
                  <Button
                    size="lg"
                    loading={offerResponding}
                    onClick={() => handleOfferRespond(false, false)}
                    style={{
                      backgroundColor: 'var(--color-primary)',
                      borderColor: 'var(--color-primary)',
                      color: 'var(--color-bg-primary)',
                      padding: '16px 48px',
                      fontSize: '1.1rem',
                      borderRadius: 'var(--radius-full)',
                    }}
                  >
                    Tengo il mio!
                  </Button>
                </motion.div>
              </>
            ) : (
              <>
                <motion.div
                  style={S.offerLabel}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Phone size={20} />
                  Il Dottore propone
                </motion.div>

                <motion.div
                  style={S.offerAmount}
                  initial={{ scale: 0, rotate: -10 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.6 }}
                >
                  {formatEuro(currentOffer.amount)}
                </motion.div>

                <motion.div
                  style={S.offerButtons}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  <Button
                    size="lg"
                    loading={offerResponding}
                    onClick={() => handleOfferRespond(true)}
                    style={{
                      backgroundColor: 'var(--color-success)',
                      borderColor: 'var(--color-success)',
                      color: '#fff',
                      padding: '16px 48px',
                      fontSize: '1.1rem',
                      borderRadius: 'var(--radius-full)',
                    }}
                  >
                    Accetto
                  </Button>
                  <Button
                    size="lg"
                    loading={offerResponding}
                    onClick={() => handleOfferRespond(false)}
                    style={{
                      backgroundColor: 'var(--color-danger)',
                      borderColor: 'var(--color-danger)',
                      color: '#fff',
                      padding: '16px 48px',
                      fontSize: '1.1rem',
                      borderRadius: 'var(--radius-full)',
                    }}
                  >
                    Rifiuto
                  </Button>
                </motion.div>
              </>
            )}
          </motion.div>
        )}

        {/* ─── FINAL ─── */}
        {phase === PHASE.FINAL && (
          <motion.div
            key="final"
            style={S.finalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              style={S.finalIcon}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.2 }}
            >
              <PartyPopper size={64} />
            </motion.div>

            <motion.h2
              style={S.finalTitle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Partita terminata!
            </motion.h2>

            {/* Determine what to show */}
            {(() => {
              const fd = finalData || game;
              if (!fd) return null;

              const didAcceptOffer = fd.offer_accepted === true;
              const didSwap = fd.swapped === true;
              const finalPrize = fd.final_prize_value;
              const playerBoxPrize = fd.player_box_prize_value;
              const lastOffer = fd.last_offer;

              if (didSwap) {
                return (
                  <>
                    <motion.p
                      style={S.finalDetail}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                    >
                      Hai scambiato il pacco! Il tuo nuovo pacco conteneva
                    </motion.p>
                    <motion.div
                      style={S.finalPrize}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 150, damping: 12, delay: 0.8 }}
                    >
                      {formatEuro(finalPrize ?? playerBoxPrize ?? 0)}
                    </motion.div>
                  </>
                );
              }

              if (didAcceptOffer && lastOffer) {
                return (
                  <>
                    <motion.p
                      style={S.finalDetail}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                    >
                      Hai accettato l'offerta del Dottore
                    </motion.p>
                    <motion.div
                      style={S.finalPrize}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 150, damping: 12, delay: 0.8 }}
                    >
                      {formatEuro(finalPrize ?? lastOffer.amount)}
                    </motion.div>
                    {playerBoxPrize !== undefined && playerBoxPrize !== null && (
                      <motion.p
                        style={{ ...S.finalDetail, marginTop: 'var(--space-md)' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2 }}
                      >
                        Il tuo pacco conteneva <strong style={{ color: 'var(--color-primary)' }}>{formatEuro(playerBoxPrize)}</strong>
                      </motion.p>
                    )}
                  </>
                );
              }

              /* No offer accepted - show player's box prize */
              return (
                <>
                  <motion.p
                    style={S.finalDetail}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    Il tuo pacco conteneva
                  </motion.p>
                  <motion.div
                    style={S.finalPrize}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 150, damping: 12, delay: 0.8 }}
                  >
                    {formatEuro(finalPrize ?? playerBoxPrize ?? 0)}
                  </motion.div>
                </>
              );
            })()}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5 }}
            >
              <Button
                size="lg"
                onClick={handlePlayAgain}
                style={{
                  marginTop: 'var(--space-lg)',
                  borderRadius: 'var(--radius-full)',
                  padding: '14px 40px',
                  letterSpacing: '0.08em',
                }}
              >
                <RotateCcw size={18} />
                Gioca ancora
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
